import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, RefreshCw, Lock, LogOut, Eye, EyeOff, Star, FileText, MapPin, ExternalLink } from 'lucide-react';
import { BlogAdmin } from './BlogAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeSection, setActiveSection] = useState('projects'); // 'projects' or 'blog'
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    type: '',
    units: '',
    status: 'In Entwicklung',
    image: '',
    description: '',
    features: [],
    visible: true
  });

  const statusOptions = ['In Entwicklung', 'Realisiert', 'Verkauft'];

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', 'true');
        setPassword('');
      } else {
        setLoginError('Falsches Passwort');
      }
    } catch (err) {
      setLoginError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/projects`);
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const seedProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/seed`, {
        method: 'POST'
      });
      const data = await response.json();
      alert(data.message);
      fetchProjects();
    } catch (err) {
      alert('Error seeding projects: ' + err.message);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          features: formData.features.length > 0 ? formData.features : []
        })
      });
      if (!response.ok) throw new Error('Failed to create project');
      await fetchProjects();
      setIsCreating(false);
      resetForm();
    } catch (err) {
      alert('Error creating project: ' + err.message);
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${editingProject.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed to update project');
      await fetchProjects();
      setEditingProject(null);
      resetForm();
    } catch (err) {
      alert('Error updating project: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sind Sie sicher, dass Sie dieses Projekt löschen möchten?')) return;
    try {
      const response = await fetch(`${API_URL}/api/projects/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete project');
      await fetchProjects();
    } catch (err) {
      alert('Error deleting project: ' + err.message);
    }
  };

  const startEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      location: project.location,
      type: project.type,
      units: project.units,
      status: project.status,
      image: project.image,
      description: project.description || '',
      features: project.features || [],
      visible: project.visible !== false
    });
    setIsCreating(false);
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingProject(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      location: '',
      type: '',
      units: '',
      status: 'In Entwicklung',
      image: '',
      description: '',
      features: [],
      visible: true
    });
  };
  
  const toggleVisibility = async (project) => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible: !project.visible })
      });
      if (!response.ok) throw new Error('Failed to update visibility');
      await fetchProjects();
    } catch (err) {
      alert('Error updating visibility: ' + err.message);
    }
  };

  const toggleFeatured = async (project) => {
    // Count currently featured projects
    const featuredCount = projects.filter(p => p.featured).length;
    
    // If trying to add a new featured project and already have 3
    if (!project.featured && featuredCount >= 3) {
      alert('Maximal 3 Projekte können auf der Startseite angezeigt werden. Bitte entfernen Sie zuerst ein anderes Projekt.');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !project.featured })
      });
      if (!response.ok) throw new Error('Failed to update featured status');
      await fetchProjects();
    } catch (err) {
      alert('Error updating featured status: ' + err.message);
    }
  };

  // Get featured count for display
  const featuredCount = projects.filter(p => p.featured).length;

  const cancelEdit = () => {
    setEditingProject(null);
    setIsCreating(false);
    resetForm();
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#264F77] to-[#1e3d5c] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#264F77]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-[#264F77]" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-[#264F77]">Admin-Bereich</h1>
            <p className="text-gray-500 mt-2">Bitte geben Sie das Passwort ein</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Passwort
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent transition-all"
                placeholder="••••••••"
                autoFocus
              />
            </div>
            
            {loginError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {loginError}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoggingIn || !password}
              className="w-full py-3 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? 'Wird überprüft...' : 'Anmelden'}
            </button>
          </form>
          
          <p className="text-center text-gray-400 text-sm mt-6">
            LIVAG AG - Projektverwaltung
          </p>
        </div>
      </div>
    );
  }

  // Show Blog Admin if that section is active
  if (activeSection === 'blog') {
    return <BlogAdmin onBack={() => setActiveSection('projects')} />;
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#264F77] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Admin-Konsole</h1>
            <p className="text-gray-300 mt-2">Projekt- und Blog-Verwaltung</p>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <LogOut size={20} className="mr-2" />
            Abmelden
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveSection('projects')}
            className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all ${
              activeSection === 'projects'
                ? 'bg-[#264F77] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
            }`}
          >
            <Star size={20} className="mr-2" />
            Projekte
          </button>
          <button
            onClick={() => setActiveSection('blog')}
            className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all ${
              activeSection === 'blog'
                ? 'bg-[#264F77] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
            }`}
          >
            <FileText size={20} className="mr-2" />
            Blog-Verwaltung
          </button>
          <button
            onClick={() => setActiveSection('seo')}
            data-testid="admin-tab-seo"
            className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all ${
              activeSection === 'seo'
                ? 'bg-[#264F77] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
            }`}
          >
            <MapPin size={20} className="mr-2" />
            Lokale SEO-Seiten
          </button>
        </div>

        {activeSection === 'projects' && (
          <>
        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={startCreate}
            className="inline-flex items-center px-4 py-2 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c] transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Neues Projekt
          </button>
          <button
            onClick={fetchProjects}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw size={20} className="mr-2" />
            Aktualisieren
          </button>
          <button
            onClick={seedProjects}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Initiale Projekte laden
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Create/Edit Form */}
        {(isCreating || editingProject) && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-[#264F77] mb-6">
              {isCreating ? 'Neues Projekt erstellen' : 'Projekt bearbeiten'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="z.B. Eichenstrasse"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Standort</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="z.B. St. Gallen"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Typ</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="z.B. Mehrfamilienhaus"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Einheiten</label>
                <input
                  type="text"
                  value={formData.units}
                  onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="z.B. 8 Wohnungen"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bild-URL</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="https://..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent"
                  placeholder="Projektbeschreibung..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.visible}
                    onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                    className="w-5 h-5 text-[#264F77] border-gray-300 rounded focus:ring-[#264F77]"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Projekt auf der Website anzeigen
                  </span>
                </label>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={isCreating ? handleCreate : handleUpdate}
                className="inline-flex items-center px-6 py-2 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c] transition-colors"
              >
                <Save size={20} className="mr-2" />
                {isCreating ? 'Erstellen' : 'Speichern'}
              </button>
              <button
                onClick={cancelEdit}
                className="inline-flex items-center px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                <X size={20} className="mr-2" />
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Projects List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-[#264F77]">
              Projekte ({projects.length})
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Startseite: {featuredCount}/3 Projekte ausgewählt
            </p>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Laden...</div>
          ) : projects.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Keine Projekte vorhanden. Klicken Sie auf "Initiale Projekte laden" um zu starten.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bild</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standort</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sichtbar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Startseite</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {projects.map((project) => (
                    <tr key={project.id} className={`hover:bg-gray-50 ${project.visible === false ? 'opacity-50' : ''}`}>
                      <td className="px-6 py-4">
                        <img
                          src={project.image}
                          alt={`${project.title} - Immobilienprojekt in ${project.location}`}
                          title={project.title}
                          loading="lazy"
                          className="w-20 h-14 object-cover rounded"
                        />
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">{project.title}</td>
                      <td className="px-6 py-4 text-gray-600">{project.location}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          project.status === 'In Entwicklung' ? 'bg-blue-100 text-blue-800' :
                          project.status === 'Realisiert' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleVisibility(project)}
                          className={`p-2 rounded-lg transition-colors ${
                            project.visible !== false
                              ? 'text-green-600 hover:bg-green-100'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                          title={project.visible !== false ? 'Sichtbar - Klicken zum Ausblenden' : 'Ausgeblendet - Klicken zum Anzeigen'}
                        >
                          {project.visible !== false ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleFeatured(project)}
                          disabled={project.visible === false}
                          className={`p-2 rounded-lg transition-colors ${
                            project.featured
                              ? 'text-yellow-500 hover:bg-yellow-100'
                              : 'text-gray-300 hover:bg-gray-100'
                          } ${project.visible === false ? 'opacity-30 cursor-not-allowed' : ''}`}
                          title={project.featured ? 'Auf Startseite - Klicken zum Entfernen' : 'Klicken um auf Startseite anzuzeigen'}
                        >
                          <Star size={18} fill={project.featured ? 'currentColor' : 'none'} />
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(project)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Bearbeiten"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(project.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {activeSection === 'seo' && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8" data-testid="admin-seo-section">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-[#264F77] mb-2">Lokale SEO-Landingpages</h2>
              <p className="text-sm text-gray-600">
                Diese Seiten sind für Suchmaschinen optimiert und in der Sitemap eingetragen,
                aber nicht im öffentlichen Footer verlinkt. Direkt aufrufbar über die URLs unten.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { label: 'Immobilien St. Gallen', path: '/immobilien-verkaufen-st-gallen' },
                { label: 'Kaufen in St. Gallen', path: '/immobilien-kaufen-st-gallen' },
                { label: 'Immobilienmakler Gossau', path: '/immobilienmakler-gossau' },
                { label: 'Haus kaufen Gossau', path: '/haus-kaufen-gossau' },
                { label: 'Immobilienmakler Wil', path: '/immobilienmakler-wil-sg' },
                { label: 'Immobilien Wil SG', path: '/immobilien-wil-sg' },
                { label: 'Immobilienmakler Herisau', path: '/immobilienmakler-herisau' },
                { label: 'Immobilienmakler Rorschach', path: '/immobilienmakler-rorschach' },
              ].map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-seo-link-${item.path.replace(/\//g, '')}`}
                  className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:border-[#264F77] hover:bg-gray-50 transition-colors group"
                >
                  <div>
                    <div className="text-sm font-medium text-[#264F77]">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{item.path}</div>
                  </div>
                  <ExternalLink size={18} className="text-gray-400 group-hover:text-[#264F77] transition-colors" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
